import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { TeacherShell } from './Layout.tsx';
import { type JoinResult, joinClassroom } from './api.ts';

/**
 * Student join-by-code (D4 web).
 *
 * Public route. The student types the 6-character code their teacher has
 * displayed on the room TV; on success they land in a "joined classroom"
 * placeholder that mounts a banner above the canvas. Real classroom
 * canvas behavior (assigned challenge, lockdown of certain tools, etc.)
 * is a separate ticket.
 */
export function StudentJoin(): JSX.Element {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState<JoinResult | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const normalized = code.trim().toUpperCase();
      const result = await joinClassroom(normalized);
      setJoined(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'request_failed';
      if (msg === 'not_found') {
        setError("That code didn't match a classroom. Double-check with your teacher?");
      } else {
        setError("Couldn't reach the server. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (joined) {
    return (
      <TeacherShell>
        <div className="mx-auto max-w-2xl px-4 py-12">
          <output
            aria-live="polite"
            className="block rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900"
          >
            <span className="block text-sm font-semibold uppercase tracking-wide">Joined</span>
            <span className="mt-2 block text-2xl font-bold">{joined.classroomName}</span>
            <span className="mt-2 block text-sm">
              You're in. The classroom canvas opens here when it's ready — for now this banner is
              the placeholder.
            </span>
          </output>
          <Link
            to="/"
            className="mt-6 inline-block rounded-2xl bg-amber-500 px-6 py-4 text-base font-semibold text-white shadow transition hover:bg-amber-600"
          >
            Open the canvas
          </Link>
        </div>
      </TeacherShell>
    );
  }

  return (
    <TeacherShell>
      <div className="mx-auto max-w-md px-4 py-12 sm:py-16">
        <div className="rounded-3xl bg-white p-6 shadow-md sm:p-8">
          <h1 className="text-3xl font-bold text-slate-900">Join your class</h1>
          <p className="mt-2 text-base text-slate-600">
            Type the 6-character code your teacher showed you.
          </p>

          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            <label className="text-sm font-semibold text-slate-700" htmlFor="code">
              Class code
              <input
                id="code"
                type="text"
                required
                inputMode="text"
                autoComplete="off"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABC234"
                className="mt-2 block w-full rounded-2xl border-2 border-slate-300 bg-white px-4 py-5 text-center font-mono text-3xl tracking-[0.5em] uppercase focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </label>
            {error && (
              <p className="text-sm text-rose-600" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              className="rounded-2xl bg-amber-500 px-6 py-4 text-lg font-semibold text-white shadow transition active:scale-95 hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Joining…' : 'Join'}
            </button>
          </form>
        </div>
      </div>
    </TeacherShell>
  );
}
